import { useRef } from "react";
import { motion, useScroll, useTransform, useSpring, useMotionValue } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { useLang } from "@/i18n";
import { scrollToId } from "./Nav";

const HERO_IMG =
  "https://images.unsplash.com/photo-1592228533283-d78f7c1cf453?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjB3YXJlaG91c2UlMjB3b3JrZXIlMjBsb2dpc3RpY3MlMjBpbnZlbnRvcnklMjBjb250YWluZXIlMjBzaGlwcGluZ3xlbnwwfHx8fDE3ODg0OTUzOTV8MA&ixlib=rb-4.1.0&q=85";

const lineReveal = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.2 + i * 0.13 },
  }),
};

export default function Hero() {
  const { t } = useLang();
  const frameRef = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rX = useSpring(useTransform(my, [-0.5, 0.5], [4, -4]), { stiffness: 120, damping: 18 });
  const rY = useSpring(useTransform(mx, [-0.5, 0.5], [-4, 4]), { stiffness: 120, damping: 18 });

  const { scrollY } = useScroll();
  const imgY = useTransform(scrollY, [0, 700], [0, 70]);

  const onMove = (e) => {
    const r = frameRef.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section id="top" className="relative overflow-hidden pt-[72px]">
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute left-1/2 top-0 h-full w-px -translate-x-[640px] bg-line/70" />
        <div className="absolute left-1/2 top-0 h-full w-px translate-x-[640px] bg-line/70" />
      </div>

      <div className="mx-auto grid max-w-[1320px] grid-cols-1 items-center gap-12 px-5 pb-16 pt-10 sm:px-8 lg:grid-cols-12 lg:gap-8 lg:pb-24 lg:pt-16">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-mono mb-6 flex items-center gap-3 text-[11px] font-bold tracking-[0.25em] text-obsidian/60"
          >
            <span className="inline-block h-2 w-2 animate-pulse-dot rounded-full bg-[#10B981]" />
            {t.hero.eyebrow}
          </motion.p>

          <h1 className="font-display font-expanded text-[clamp(2.6rem,7.2vw,5.6rem)] font-black uppercase leading-[0.98] tracking-tight text-obsidian">
            {t.hero.lines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 2 ? "text-stroke" : ""}`}
                  custom={i}
                  variants={lineReveal}
                  initial="hidden"
                  animate="visible"
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.65 }}
            className="mt-7 max-w-xl text-base leading-relaxed text-obsidian/70 sm:text-lg"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.8 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-quote-button"
              onClick={() => scrollToId("quote")}
              className="group flex items-center gap-2 rounded-full bg-brand px-7 py-4 text-sm font-bold text-white shadow-[0_10px_30px_-10px_rgba(232,57,74,0.55)] transition-all duration-300 hover:bg-obsidian hover:text-paper"
            >
              {t.hero.ctaPrimary}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button
              data-testid="hero-cta-process-button"
              onClick={() => scrollToId("process")}
              className="group flex items-center gap-2 rounded-full border border-obsidian/20 px-7 py-4 text-sm font-bold text-obsidian transition-colors duration-300 hover:border-obsidian hover:bg-obsidian hover:text-paper"
            >
              {t.hero.ctaSecondary}
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
            </button>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96, clipPath: "inset(8% 8% 8% 8%)" }}
          animate={{ opacity: 1, scale: 1, clipPath: "inset(0% 0% 0% 0%)" }}
          transition={{ duration: 1.1, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5"
        >
          <motion.div
            ref={frameRef}
            onMouseMove={onMove}
            onMouseLeave={() => {
              mx.set(0);
              my.set(0);
            }}
            style={{ rotateX: rX, rotateY: rY, transformPerspective: 900 }}
            className="relative overflow-hidden rounded-2xl border border-line bg-white shadow-[0_30px_60px_-30px_rgba(15,23,42,0.35)]"
          >
            <motion.img
              src={HERO_IMG}
              alt="ON-ALL LLC New Jersey warehouse"
              style={{ y: imgY }}
              className="h-[380px] w-full scale-[1.12] object-cover sm:h-[460px]"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-obsidian/50 via-transparent to-transparent" />
            <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between rounded-xl border border-white/20 bg-obsidian/70 px-4 py-3 backdrop-blur-md">
              <span className="font-mono text-[10px] font-bold tracking-[0.2em] text-paper">
                40.2171° N — 74.7429° W
              </span>
              <span className="flex items-center gap-2 font-mono text-[10px] font-bold tracking-[0.2em] text-[#10B981]">
                <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-[#10B981]" />
                {t.hero.badge}
              </span>
            </div>
          </motion.div>
        </motion.div>
      </div>

      <div className="mx-auto max-w-[1320px] px-5 sm:px-8">
        <div className="grid grid-cols-2 border-t border-line lg:grid-cols-4">
          {t.hero.stats.map((s, i) => (
            <motion.div
              key={s.label}
              data-testid={`hero-stat-${i}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1 + i * 0.1 }}
              className="border-r border-b border-line px-5 py-6 last:border-r-0 lg:border-b-0 lg:px-8 lg:py-8"
            >
              <p className="font-mono text-[10px] tracking-[0.2em] text-obsidian/50">{s.meta}</p>
              <p className="font-display font-expanded mt-2 text-3xl font-black text-obsidian lg:text-5xl">{s.value}</p>
              <p className="mt-1 text-xs font-semibold text-obsidian/60 sm:text-sm">{s.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
